"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import WaveDropPartContentMedias from "./WaveDropPartContentMedias";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ReferencedNft } from "@/entities/IDrop";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropPartContentMarkdown from "./WaveDropPartContentMarkdown";
import WaveDropPartContentAttachments from "./WaveDropPartContentAttachments";
import { ImageScale } from "@/helpers/image.helpers";
import type { DropContentPresentation } from "./dropContentPresentation";
import { DropImageGalleryProvider } from "@/components/drops/view/part/DropImageGalleryProvider";
import { buildDropImageGalleryItems } from "@/components/drops/view/part/dropImageGallery";
import { getQuorumProposalCompactBodyGalleryKeyPrefix, getQuorumProposalCompactSectionKey, QUORUM_PROPOSAL_COMPACT_SUMMARY_KEY } from "@/components/waves/quorum/QuorumProposalCompactContent";
import { parseQuorumProposalMarkdown } from "@/components/waves/quorum/quorumProposalMarkdown";
interface WaveDropPartContentProps {
  readonly mentionedUsers: ApiDropMentionedUser[];
  readonly mentionedGroups?: ApiDropGroupMention[] | undefined;
  readonly mentionedWaves: ApiMentionedWave[];
  readonly referencedNfts: ReferencedNft[];
  readonly wave: ApiWaveMin;
  readonly activePart: ApiDropPart;
  readonly havePreviousPart: boolean;
  readonly haveNextPart: boolean;
  readonly isStorm: boolean;
  readonly activePartIndex: number;
  readonly setActivePartIndex: (index: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isEditing?: boolean | undefined;
  readonly isSaving?: boolean | undefined;
  readonly onSave?: ((newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly drop?: ApiDrop | undefined;
  readonly isCompetitionDrop?: boolean | undefined;
  readonly mediaImageScale?: ImageScale | undefined;
  readonly mediaContainerHeightClassName?: string | undefined;
  readonly fullWidthMedia?: boolean | undefined;
  readonly fullWidthLinkPreviews?: boolean | undefined;
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
interface RenderedBodyMarkdown {
  readonly content: string | null;
  readonly bodyGalleryKeyPrefix?: string | undefined;
}
const WaveDropPartContent: React.FC<WaveDropPartContentProps> = t0 => {
  const $ = _c(90);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    wave,
    activePart,
    havePreviousPart,
    haveNextPart,
    isStorm,
    activePartIndex,
    setActivePartIndex,
    onQuoteClick,
    isEditing: t2,
    isSaving: t3,
    onSave,
    onCancel,
    drop,
    isCompetitionDrop: t4,
    mediaImageScale: t5,
    mediaContainerHeightClassName,
    fullWidthMedia: t6,
    fullWidthLinkPreviews: t7,
    onLinkCardActionsActiveChange,
    contentPresentation: t8,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  let t9;
  if ($[0] !== t1) {
    t9 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const mentionedGroups = t9;
  const isEditing = t2 === undefined ? false : t2;
  const isSaving = t3 === undefined ? false : t3;
  const isCompetitionDrop = t4 === undefined ? false : t4;
  const mediaImageScale = t5 === undefined ? ImageScale.AUTOx450 : t5;
  const fullWidthMedia = t6 === undefined ? false : t6;
  const fullWidthLinkPreviews = t7 === undefined ? false : t7;
  const contentPresentation = t8 === undefined ? "default" : t8;
  const contentRef = React.useRef(null);
  const [quorumCompactDetailsVisible, setQuorumCompactDetailsVisible] = React.useState(false);
  let t10;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[2] = t10;
  } else {
    t10 = $[2];
  }
  const [quorumCompactOpenSectionKeys, setQuorumCompactOpenSectionKeys] = React.useState(t10);
  let t11;
  if ($[3] !== activePart.content || $[4] !== contentPresentation || $[5] !== isEditing) {
    t11 = contentPresentation === "quorumCompact" && !isEditing ? parseQuorumProposalMarkdown(activePart.content) : null;
    $[3] = activePart.content;
    $[4] = contentPresentation;
    $[5] = isEditing;
    $[6] = t11;
  } else {
    t11 = $[6];
  }
  const compactProposal = t11;
  let t12;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setQuorumCompactDetailsVisible(false);
      setQuorumCompactOpenSectionKeys([]);
    };
    $[7] = t12;
  } else {
    t12 = $[7];
  }
  let t13;
  if ($[8] !== activePart.content || $[9] !== contentPresentation || $[10] !== isEditing) {
    t13 = [activePart.content, contentPresentation, isEditing];
    $[8] = activePart.content;
    $[9] = contentPresentation;
    $[10] = isEditing;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  React.useEffect(t12, t13);
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = areDetailsVisible => {
      setQuorumCompactDetailsVisible(areDetailsVisible);
      if (!areDetailsVisible) {
        setQuorumCompactOpenSectionKeys([]);
      }
    };
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const handleQuorumCompactDetailsVisibleChange = t14;
  let t15;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = (sectionKey, isOpen) => {
      setQuorumCompactOpenSectionKeys(currentKeys => {
        if (isOpen) {
          return currentKeys.includes(sectionKey) ? currentKeys : [...currentKeys, sectionKey];
        }
        return currentKeys.filter(key => key !== sectionKey);
      });
    };
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const handleQuorumCompactSectionOpenChange = t15;
  let t16;
  if ($[14] !== activePart.content || $[15] !== compactProposal || $[16] !== contentPresentation || $[17] !== isEditing || $[18] !== quorumCompactDetailsVisible || $[19] !== quorumCompactOpenSectionKeys) {
    bb0: {
      if (isEditing) {
        let t17;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t17 = [];
          $[21] = t17;
        } else {
          t17 = $[21];
        }
        t16 = t17;
        break bb0;
      }
      if (contentPresentation !== "quorumCompact" || !compactProposal) {
        let t17;
        if ($[22] !== activePart.content) {
          t17 = [{
            content: activePart.content
          }];
          $[22] = activePart.content;
          $[23] = t17;
        } else {
          t17 = $[23];
        }
        t16 = t17;
        break bb0;
      }
      const bodyMarkdowns = [{
        content: compactProposal.summaryMarkdown,
        bodyGalleryKeyPrefix: getQuorumProposalCompactBodyGalleryKeyPrefix(QUORUM_PROPOSAL_COMPACT_SUMMARY_KEY)
      }];
      if (!quorumCompactDetailsVisible) {
        t16 = bodyMarkdowns;
        break bb0;
      }
      let t17;
      if ($[24] !== quorumCompactOpenSectionKeys) {
        t17 = new Set(quorumCompactOpenSectionKeys);
        $[24] = quorumCompactOpenSectionKeys;
        $[25] = t17;
      } else {
        t17 = $[25];
      }
      const openSectionKeySet = t17;
      compactProposal.sections.forEach((section, index) => {
        const sectionKey_0 = getQuorumProposalCompactSectionKey(section, index);
        if (openSectionKeySet.has(sectionKey_0)) {
          bodyMarkdowns.push({
            content: section.markdown,
            bodyGalleryKeyPrefix: getQuorumProposalCompactBodyGalleryKeyPrefix(sectionKey_0)
          });
        }
      });
      t16 = bodyMarkdowns;
    }
    $[14] = activePart.content;
    $[15] = compactProposal;
    $[16] = contentPresentation;
    $[17] = isEditing;
    $[18] = quorumCompactDetailsVisible;
    $[19] = quorumCompactOpenSectionKeys;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const renderedBodyMarkdowns = t16;
  let t17;
  if ($[26] !== activePart.media) {
    t17 = activePart.media.map(_temp);
    $[26] = activePart.media;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const galleryPartMedias = t17;
  let t18;
  if ($[28] !== activePart.content || $[29] !== galleryPartMedias || $[30] !== renderedBodyMarkdowns) {
    t18 = buildDropImageGalleryItems({
      bodyMarkdowns: renderedBodyMarkdowns,
      partContent: activePart.content,
      partMedias: galleryPartMedias
    });
    $[28] = activePart.content;
    $[29] = galleryPartMedias;
    $[30] = renderedBodyMarkdowns;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const galleryItems = t18;
  const memoizedMentionedUsers = mentionedUsers;
  const memoizedMentionedWaves = mentionedWaves;
  const memoizedMentionedGroups = mentionedGroups;
  const memoizedReferencedNfts = referencedNfts;
  let t19;
  if ($[32] !== activePartIndex || $[33] !== haveNextPart || $[34] !== havePreviousPart || $[35] !== setActivePartIndex) {
    t19 = direction => {
      const isPrevious = direction === "previous";
      const isDisabled = isPrevious ? !havePreviousPart : !haveNextPart;
      const onClick = () => setActivePartIndex(activePartIndex + (isPrevious ? -1 : 1));
      return <button disabled={isDisabled} className={`${isDisabled ? "tw-cursor-default tw-border-iron-700 tw-text-iron-700" : "tw-border-primary-400 tw-text-primary-400 hover:tw-bg-primary-400 hover:tw-text-white"} tw-flex tw-size-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-bg-transparent tw-transition tw-duration-300 tw-ease-out sm:tw-size-6`} onClick={e => {
        e.stopPropagation();
        onClick();
      }} aria-label={`${isPrevious ? "Previous" : "Next"} part`}><svg className="tw-size-5 tw-flex-shrink-0 sm:tw-size-4" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d={isPrevious ? "M15.75 19.5 8.25 12l7.5-7.5" : "m8.25 4.5 7.5 7.5-7.5 7.5"} /></svg></button>;
    };
    $[32] = activePartIndex;
    $[33] = haveNextPart;
    $[34] = havePreviousPart;
    $[35] = setActivePartIndex;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const renderNavigationButton = t19;
  let t20;
  if ($[37] !== isStorm || $[38] !== renderNavigationButton) {
    t20 = isStorm && <div className="tw-mb-3 tw-flex tw-justify-between tw-space-x-3 md:tw-hidden">{renderNavigationButton("previous")}{renderNavigationButton("next")}</div>;
    $[37] = isStorm;
    $[38] = renderNavigationButton;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== isStorm || $[41] !== renderNavigationButton) {
    t21 = isStorm && <div className="tw-hidden md:tw-block">{renderNavigationButton("previous")}</div>;
    $[40] = isStorm;
    $[41] = renderNavigationButton;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== activePart || $[44] !== contentPresentation || $[45] !== drop || $[46] !== embedDepth || $[47] !== embedPath || $[48] !== fullWidthLinkPreviews || $[49] !== isEditing || $[50] !== isSaving || $[51] !== maxEmbedDepth || $[52] !== memoizedMentionedGroups || $[53] !== memoizedMentionedUsers || $[54] !== memoizedMentionedWaves || $[55] !== memoizedReferencedNfts || $[56] !== onCancel || $[57] !== onLinkCardActionsActiveChange || $[58] !== onQuoteClick || $[59] !== onSave || $[60] !== quorumCompactDetailsVisible || $[61] !== quorumCompactOpenSectionKeys || $[62] !== quotePath || $[63] !== wave) {
    t22 = <div><WaveDropPartContentMarkdown mentionedUsers={memoizedMentionedUsers} mentionedGroups={memoizedMentionedGroups} mentionedWaves={memoizedMentionedWaves} referencedNfts={memoizedReferencedNfts} part={activePart} wave={wave} onQuoteClick={onQuoteClick} isEditing={isEditing} isSaving={isSaving} onSave={onSave} onCancel={onCancel} drop={drop} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} fullWidthLinkPreviews={fullWidthLinkPreviews} quorumCompactDetailsVisible={quorumCompactDetailsVisible} onQuorumCompactDetailsVisibleChange={handleQuorumCompactDetailsVisibleChange} quorumCompactOpenSectionKeys={quorumCompactOpenSectionKeys} onQuorumCompactSectionOpenChange={handleQuorumCompactSectionOpenChange} /></div>;
    $[43] = activePart;
    $[44] = contentPresentation;
    $[45] = drop;
    $[46] = embedDepth;
    $[47] = embedPath;
    $[48] = fullWidthLinkPreviews;
    $[49] = isEditing;
    $[50] = isSaving;
    $[51] = maxEmbedDepth;
    $[52] = memoizedMentionedGroups;
    $[53] = memoizedMentionedUsers;
    $[54] = memoizedMentionedWaves;
    $[55] = memoizedReferencedNfts;
    $[56] = onCancel;
    $[57] = onLinkCardActionsActiveChange;
    $[58] = onQuoteClick;
    $[59] = onSave;
    $[60] = quorumCompactDetailsVisible;
    $[61] = quorumCompactOpenSectionKeys;
    $[62] = quotePath;
    $[63] = wave;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== activePart || $[66] !== fullWidthMedia || $[67] !== isCompetitionDrop || $[68] !== mediaContainerHeightClassName || $[69] !== mediaImageScale) {
    t23 = !!activePart.media.length && <WaveDropPartContentMedias activePart={activePart} isCompetitionDrop={isCompetitionDrop} imageScale={mediaImageScale} mediaContainerHeightClassName={mediaContainerHeightClassName} fullWidthMedia={fullWidthMedia} />;
    $[65] = activePart;
    $[66] = fullWidthMedia;
    $[67] = isCompetitionDrop;
    $[68] = mediaContainerHeightClassName;
    $[69] = mediaImageScale;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== activePart.attachments) {
    t24 = activePart.attachments ?? [];
    $[71] = activePart.attachments;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== t24) {
    t25 = <WaveDropPartContentAttachments attachments={t24} />;
    $[73] = t24;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t22 || $[76] !== t23 || $[77] !== t25) {
    t26 = <div className="tw-h-full tw-w-full" ref={contentRef}>{t22}{t23}{t25}</div>;
    $[75] = t22;
    $[76] = t23;
    $[77] = t25;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== galleryItems || $[80] !== t26) {
    t27 = <DropImageGalleryProvider items={galleryItems}>{t26}</DropImageGalleryProvider>;
    $[79] = galleryItems;
    $[80] = t26;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== isStorm || $[83] !== renderNavigationButton) {
    t28 = isStorm && <div className="tw-hidden md:tw-block">{renderNavigationButton("next")}</div>;
    $[82] = isStorm;
    $[83] = renderNavigationButton;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== t20 || $[86] !== t21 || $[87] !== t27 || $[88] !== t28) {
    t29 = <div className="tw-w-full"><div className="tw-flex tw-w-full tw-flex-col tw-justify-between md:tw-flex-row md:tw-gap-x-3">{t20}{t21}{t27}{t28}</div></div>;
    $[85] = t20;
    $[86] = t21;
    $[87] = t27;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  return t29;
};
export default WaveDropPartContent;
function _temp(media) {
  return {
    mimeType: media.mime_type,
    mediaSrc: media.url
  };
}